import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Tooltip, InputAdornment,
} from '@mui/material';
import { FixedSizeList } from 'react-window';
import PeopleIcon from '@mui/icons-material/People';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import LabelIcon from '@mui/icons-material/Label';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  accent: '#2dd4bf', accentDark: '#14b8a6',
  accentDim: 'rgba(45,212,191,0.10)', accentBorder: 'rgba(45,212,191,0.22)',
  bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
  text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
  danger: '#ef4444', dangerDark: '#dc2626',
};

const COLS = [
  { label: '#',              flex: '0 0 44px',  align: 'left'  },
  { label: 'First Name',     flex: '1 1 0',     align: 'left'  },
  { label: 'Last Name',      flex: '1 1 0',     align: 'left'  },
  { label: 'User Name',      flex: '1 1 0',     align: 'left'  },
  { label: 'Contact',        flex: '0 0 120px', align: 'left'  },
  { label: 'Email',          flex: '1 1 0',     align: 'left'  },
  { label: '',               flex: '0 0 90px',  align: 'right' },
];

const ROW_HEIGHT  = 36;
const LIST_HEIGHT = 500;

const rowBase = {
  display: 'flex', alignItems: 'center', width: '100%',
  boxSizing: 'border-box', borderBottom: `1px solid ${C.cardBorder}`,
};

function ColCell({ col, children, muted, bold, color }) {
  return (
    <div style={{
      flex: col.flex, minWidth: 0, padding: '0 8px', fontSize: '12px',
      color: color || (muted ? C.muted : C.text), fontWeight: bold ? 600 : 400,
      textAlign: col.align, overflow: 'hidden', whiteSpace: 'nowrap',
      textOverflow: 'ellipsis', boxSizing: 'border-box',
    }}>
      {children}
    </div>
  );
}

const UserRow = React.memo(({ index, style, data: listData }) => {
  const { items, onDelete, onActivate, hasEdit, hasDelete } = listData;
  const item = items[index];
  const isEven = index % 2 === 0;

  return (
    <div
      style={{
        ...style, ...rowBase,
        backgroundColor: isEven ? C.card : '#f7fdfc',
        transition: 'background-color 0.15s',
        opacity: item.IsActive ? 1 : 0.55,
      }}
      onMouseEnter={e => { e.currentTarget.style.backgroundColor = C.accentDim; }}
      onMouseLeave={e => { e.currentTarget.style.backgroundColor = isEven ? C.card : '#f7fdfc'; }}
    >
      <ColCell col={COLS[0]} muted>{index + 1}</ColCell>
      <ColCell col={COLS[1]}>{item.FirstName}</ColCell>
      <ColCell col={COLS[2]}>{item.LastName}</ColCell>
      <ColCell col={COLS[3]} bold>{item.UserName}</ColCell>
      <ColCell col={COLS[4]}>{item.ContactNumber}</ColCell>
      <ColCell col={COLS[5]}>{item.Email}</ColCell>
      <ColCell col={COLS[6]}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '2px', alignItems: 'center' }}>
          <Tooltip title="Details" arrow>
            <Link to={`/userDetail/${item.Id}`}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '4px', color: '#f59e0b', textDecoration: 'none' }}>
              <InfoIcon style={{ fontSize: 15 }} />
            </Link>
          </Tooltip>
          {hasEdit && item.IsActive && (
            <Tooltip title="Edit" arrow>
              <Link to={`/userEdit/${item.Id}`}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '4px', color: '#7c3aed', textDecoration: 'none' }}>
                <EditIcon style={{ fontSize: 15 }} />
              </Link>
            </Tooltip>
          )}
          {hasDelete && item.IsActive && (
            <Tooltip title="Delete" arrow>
              <button onClick={() => onDelete(item.Id)}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '4px', color: C.danger, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <DeleteIcon style={{ fontSize: 15 }} />
              </button>
            </Tooltip>
          )}
          {!item.IsActive && (
            <Tooltip title="Activate" arrow>
              <button onClick={() => onActivate(item.Id)}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '4px', color: '#22c55e', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <CheckCircleIcon style={{ fontSize: 15 }} />
              </button>
            </Tooltip>
          )}
        </div>
      </ColCell>
    </div>
  );
});

function UserIndex() {
  const { isAuthenticated, companyId, branchId, userId: authUserId, logout, token, permissions } = useAuth();

  const hasCreate = permissions.includes('Create Administration');
  const hasEdit   = permissions.includes('Edit Administration');
  const hasDelete = permissions.includes('Delete Administration');

  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [searchQuery, setSearchQuery]       = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [deleteId, setDeleteId]     = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const debounceRef = useRef(null);
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(val), 200);
  };

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    fetchTableData();
  }, []);

  const fetchTableData = async () => {
    setLoading(true);
    try {
      const res = await fetchData('UserApi', `/${companyId}/${branchId}`, token);
      if (res.responseCode === '1111') setData(res.response_data);
    } catch (err) { console.error('Error fetching users:', err); }
    finally { setLoading(false); }
  };

  const filteredData = useMemo(() => {
    if (!debouncedQuery.trim()) return data;
    const q = debouncedQuery.toLowerCase();
    return data.filter(item =>
      item.FirstName?.toString().toLowerCase().includes(q) ||
      item.LastName?.toString().toLowerCase().includes(q) ||
      item.UserName?.toString().toLowerCase().includes(q) ||
      item.Email?.toString().toLowerCase().includes(q)
    );
  }, [data, debouncedQuery]);

  const itemData = useMemo(() => ({
    items: filteredData,
    onDelete: (id) => { setDeleteId(id); setDeleteOpen(true); },
    onActivate: (id) => activateData('UserApi', `${id}`, token).then(fetchTableData).catch(console.error),
    hasEdit, hasDelete,
  }), [filteredData, hasEdit, hasDelete]);

  const closeDelete = useCallback(() => { setDeleteId(null); setDeleteOpen(false); }, []);
  const handleDelete = async () => {
    try {
      await deleteData('UserApi', `${deleteId}`, token);
      closeDelete();
      fetchTableData();
    } catch (err) { console.error('Error deleting:', err); }
  };

  const btnSecondary = { fontSize: '11px', fontWeight: 600, textTransform: 'none', color: C.muted, bgcolor: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: '7px', px: 1.4, py: 0.5, '&:hover': { bgcolor: C.accentDim, borderColor: C.accentBorder, color: C.text } };
  const btnDanger    = { fontSize: '11px', fontWeight: 700, textTransform: 'none', bgcolor: C.danger, color: '#fff', borderRadius: '7px', px: 1.6, py: 0.5, '&:hover': { bgcolor: C.dangerDark } };

  return (
    <Box sx={{ bgcolor: C.bg, p: '6px' }}>

      {/* ── Page header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <PeopleIcon sx={{ fontSize: 16, color: C.accent }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '13px', color: C.text }}>
            Users
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField size="small" placeholder="Search name / email…" value={searchQuery} onChange={handleSearchChange}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14, color: C.muted }} /></InputAdornment> }}
            sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: '20px', fontSize: '12px', height: '28px', bgcolor: C.card, '& fieldset': { borderColor: C.cardBorder }, '&:hover fieldset': { borderColor: C.accentBorder }, '&.Mui-focused fieldset': { borderColor: C.accent, borderWidth: 1.5 } } }} />
          {hasCreate && (
            <Link to="/userCreate"
              style={{ height: '28px', padding: '0 14px', borderRadius: '7px', border: 'none', backgroundColor: C.accent, color: '#fff', fontWeight: 700, fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', boxShadow: C.shadow, textDecoration: 'none', fontFamily: 'inherit' }}>
              <AddIcon sx={{ fontSize: 13 }} /> Create
            </Link>
          )}
        </Box>
      </Box>

      {/* ── Table card ── */}
      <Box sx={{ bgcolor: C.card, borderRadius: '10px', boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: '12px', py: '7px', borderBottom: `1px solid ${C.cardBorder}`, bgcolor: `${C.accent}0d` }}>
          <LabelIcon sx={{ fontSize: 15, color: C.accent }} />
          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
            User List
          </Typography>
          <Box sx={{ ml: 'auto' }}>
            <Typography sx={{ fontSize: '10px', color: C.muted }}>
              {filteredData.length} of {data.length} record{data.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: C.accent }} />
          </Box>
        ) : (
          <div style={{ width: '100%' }}>
            <div style={{ ...rowBase, backgroundColor: '#f8fafa', borderBottom: `2px solid ${C.cardBorder}` }}>
              {COLS.map((col, i) => (
                <div key={i} style={{ flex: col.flex, minWidth: 0, padding: '7px 8px', fontSize: '10px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: col.align, boxSizing: 'border-box' }}>
                  {col.label}
                </div>
              ))}
            </div>
            {filteredData.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5, color: C.muted, fontSize: '12px' }}>
                {data.length === 0 ? 'No users found' : 'No records match your search'}
              </Box>
            ) : (
              <FixedSizeList height={Math.min(LIST_HEIGHT, filteredData.length * ROW_HEIGHT)} itemCount={filteredData.length} itemSize={ROW_HEIGHT} width="100%" itemData={itemData} overscanCount={8}>
                {UserRow}
              </FixedSizeList>
            )}
          </div>
        )}
      </Box>

      {/* ── Delete Dialog ── */}
      <Dialog open={deleteOpen} onClose={closeDelete} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
        <DialogTitle sx={{ fontSize: '13px', fontWeight: 800, color: C.text, borderBottom: `1px solid ${C.cardBorder}`, pb: 0.5 }}>Delete User?</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <DialogContentText sx={{ fontSize: '12px', color: C.muted }}>Are you sure you want to delete this user? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 0.8 }}>
          <Button onClick={closeDelete} sx={btnSecondary}>Cancel</Button>
          <Button onClick={handleDelete} sx={btnDanger} startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}>Delete</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default UserIndex;
